import { type ChangeEvent, type ReactNode, useState } from "react";
import * as idUtils from "@/common/utils/idUtils";
import {
  CHECKLIST,
  CHECKLIST_BOX,
  CHECKLIST_BOX_INPUT,
  CHECKLIST_BOX_LABEL,
  CHECKLIST_BOX_WRAPPER,
  CHECKLIST_ITEM,
  CHECKLIST_LABEL,
  cx,
} from "@/components/shared/checklistClasses";
import type { ExperienceId } from "@/types/experience";

export interface ChecklistItem {
  id: ExperienceId;
  title: string;
  checked: boolean;
}

export interface ChecklistGroup {
  title?: string;
  subtitle?: string;
  items: ChecklistItem[];
}

export interface ChecklistData {
  groups: ChecklistGroup[];
}

export interface CheckedChange {
  id: ExperienceId;
  checked: boolean;
}

type ChecklistProps = {
  listData: ChecklistData;
  hasChecks?: boolean;
  className?: string;
  onCheckedChanged?: (changed: CheckedChange[]) => void;
};

export default function Checklist({
  listData,
  hasChecks,
  className,
  onCheckedChanged,
}: ChecklistProps) {
  const [listId] = useState(() => idUtils.genLocal());

  const setAllChecked = (checked: boolean) => {
    const changed: CheckedChange[] = [];

    for (let n = 0, maxn = listData.groups.length; n < maxn; n++) {
      const group = listData.groups[n];
      for (let m = 0, maxm = group.items.length; m < maxm; m++) {
        const item = group.items[m];
        if (item.checked !== checked) {
          changed.push({
            id: item.id,
            checked: checked,
          });
        }
      }
    }
    if (changed && onCheckedChanged) {
      onCheckedChanged(changed);
    }
  };

  const onListItemClicked = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.nodeName === "INPUT") {
      const input = e.target;
      const id = input.getAttribute("data-id");

      if (id === "all") {
        setAllChecked(input.checked);
      } else {
        const numId = Number.parseInt(id ?? "");
        if (onCheckedChanged) {
          onCheckedChanged([{ id: numId, checked: input.checked }]);
        }
      }
    }
  };

  const getHeader = (
    headerClassName: string,
    group: ChecklistGroup,
    n: number,
  ) => {
    const t = group.title ? <h2>{group.title}</h2> : null;
    const s = group.subtitle ? <h3>{group.subtitle}</h3> : null;
    return t || s ? (
      <div key={`checkbox-header-${listId}-${n}`} className={headerClassName}>
        {t}
        {s}
      </div>
    ) : null;
  };

  const getListItem = (
    itemClassName: string,
    title: string,
    isChecked: boolean,
    n: number,
    itemId: number | string,
    inList: boolean,
  ) => {
    const id = `checkbox-item-${listId}-${n}`;
    const labelId = `${id}-label`;
    // The "Select All" row sits outside ul.checklist, so it gets none of the list styles.
    const ls = (classes: string) => (inList ? classes : undefined);

    if (hasChecks) {
      return (
        <li key={id} className={cx(itemClassName, ls(CHECKLIST_ITEM))}>
          <div className={cx("checkbox-wrapper", ls(CHECKLIST_BOX_WRAPPER))}>
            <div className={cx("checkbox", ls(CHECKLIST_BOX))}>
              <input
                className={ls(CHECKLIST_BOX_INPUT)}
                type="checkbox"
                data-id={itemId}
                id={id}
                aria-labelledby={labelId}
                checked={isChecked}
                onChange={onListItemClicked}
              />
              <label
                className={ls(CHECKLIST_BOX_LABEL)}
                htmlFor={id}
                aria-labelledby={labelId}
              ></label>
            </div>
          </div>
          <label
            className={cx("label", ls(CHECKLIST_LABEL))}
            htmlFor={id}
            id={labelId}
          >
            {title}
          </label>
        </li>
      );
    } else {
      return (
        <li key={id} className={cx(itemClassName, ls(CHECKLIST_ITEM))}>
          <span className={cx("label tw:cursor-default", ls(CHECKLIST_LABEL))}>
            {title}
          </span>
        </li>
      );
    }
  };

  const hasHeaders = listData.groups.length > 1;
  const content: ReactNode[] = [];
  let allChecked = true;
  let count = 0;

  for (let n = 0, maxn = listData.groups.length; n < maxn; n++) {
    const group = listData.groups[n];
    if (hasHeaders) {
      content.push(getHeader("checklist-header", group, n));
    }
    const list = [];
    for (let m = 0, maxm = group.items.length; m < maxm; m++) {
      const item = group.items[m];
      const isChecked = item.checked;
      allChecked = isChecked && allChecked;
      list.push(
        getListItem(
          `ingredient checkbox-${n}-${m}`,
          item.title,
          isChecked,
          count,
          item.id,
          true,
        ),
      );
      count++;
    }

    if (list.length > 0) {
      content.push(
        <ul
          key={`checklist-${listId}-${n}`}
          className={cx("checklist", CHECKLIST)}
        >
          {list}
        </ul>,
      );
    }
  }

  if (count > 1 && hasChecks) {
    content.unshift(
      getListItem(
        "checkbox-all highlight-fg-1",
        "Select All",
        allChecked,
        count,
        "all",
        false,
      ),
    );
  }

  const classNames = ["checklist-wrapper"];
  if (className) {
    classNames.push(className);
  }

  return <div className={classNames.join(" ")}>{content}</div>;
}
